import { Component, Input, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { Subject } from 'rxjs';
import { AssetMaintenanceDetailsResponse } from '../../../models/maintenance-overview-response.model';
import { MaintenanceReason } from '../../../../../_shared/models/maintenance.model';
import { ServiceTypes } from '../../../../../_shared/enums/service-type.enum';
import { PriorityLevel } from '../../../../../_shared/enums/priority-levels.enum';
import { MaintenanceSource } from '../../../../../_shared/enums/maintenance-source.enum';
import { MaintenanceOverviewService } from '../../../services/maintenance-overview.service';
import { MaintenanceService } from '../../../../../_shared/services/maintenance.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { ToastrService } from 'ngx-toastr';
import { EditMaintenanceRequest } from '../../../models/maintenance-overview-request.model';
import { LookupWorkshop } from '../../../../../_shared/models/lookup.model';
import { startOfDay } from 'date-fns';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-edit-maintenance-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './edit-maintenance-modal.component.html',
  styleUrl: './edit-maintenance-modal.component.scss'
})
export class EditMaintenanceModalComponent implements OnDestroy {
  private _maintenanceData!: AssetMaintenanceDetailsResponse;

  @Input()
  set maintenanceData(value: AssetMaintenanceDetailsResponse) {
    this._maintenanceData = value;
    if (value) {
      this.loadMaintenanceDataAsync();
    }
  }

  get maintenanceData(): AssetMaintenanceDetailsResponse {
    return this._maintenanceData;
  }

  editForm: FormGroup;
  isLoading = false;
  isLoadingReasons = false;
  isLoadingWorkshops = false;
  dueDate = '';
  hasChanges = false;
  private initialFormValues: any = null;

  private transloco = inject(TranslocoService);

  maintenanceReasons: MaintenanceReason[] = [];
  maintenanceSourceLabels: Record<MaintenanceSource, string> = {
    [MaintenanceSource.OEM]: this.transloco.translate('maintenanceOverview.source.oem'),
    [MaintenanceSource.Custom]: this.transloco.translate('maintenanceOverview.source.custom'),
    [MaintenanceSource.System]: this.transloco.translate('maintenanceOverview.source.system'),
    [MaintenanceSource.DriverApp]: this.transloco.translate('maintenanceOverview.source.driverApp'),
    [MaintenanceSource.ComplianceApp]: this.transloco.translate('maintenanceOverview.source.complianceApp'),
    [MaintenanceSource.CanBus]: this.transloco.translate('maintenanceOverview.source.canBus')
  };
  serviceTypeLabels: Record<ServiceTypes, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('maintenanceOverview.serviceType.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('maintenanceOverview.serviceType.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('maintenanceOverview.serviceType.major')
  };
  priorityLevels: Record<PriorityLevel, string> = {
    [PriorityLevel.Low]: this.transloco.translate('maintenanceOverview.priority.low'),
    [PriorityLevel.Medium]: this.transloco.translate('maintenanceOverview.priority.medium'),
    [PriorityLevel.High]: this.transloco.translate('maintenanceOverview.priority.high')
  };

  serviceTypeOptions = Object.entries(this.serviceTypeLabels).map(([value, label]) => ({
    value: +value as ServiceTypes,
    label
  }));
  priorityLevelOptions = Object.entries(this.priorityLevels).map(([value, label]) => ({
    value: +value as PriorityLevel,
    label
  }));

  workshops: LookupWorkshop[] = [];

  selectedServiceType = '';
  selectedWorkshop = '';
  selectedPriorityLevel = '';
  selectedMaintenanceReason = '';

  private maintenanceReasonsSubject: Subject<void> = new Subject<void>();
  private workshopsSubject: Subject<void> = new Subject<void>();

  private maintenanceService = inject(MaintenanceOverviewService);
  private sharedMaintenanceService = inject(MaintenanceService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);

  constructor(private activeModal: NgbActiveModal, private fb: FormBuilder) {
    this.editForm = this.fb.group({
      assetName: [{ value: '', disabled: true }],
      make: [{ value: '', disabled: true }],
      model: [{ value: '', disabled: true }],
      source: [{ value: '', disabled: true }],
      interval: [{ value: '', disabled: true }],
      distanceOver: [{ value: '', disabled: true }],
      serviceType: ['', Validators.required],
      maintenanceReason: ['', Validators.required],
      workshop: [null as number | null, Validators.required],
      dueDate: ['', Validators.required],
      priorityLevel: [''],
      taskDescription: ['']
    });
  }

  ngOnDestroy(): void {
    this.maintenanceReasonsSubject.next();
    this.maintenanceReasonsSubject.complete();
    this.workshopsSubject.next();
    this.workshopsSubject.complete();
  }

  get serviceTypeLabel(): string {
    return this.selectedServiceType || this.transloco.translate('maintenanceOverview.modals.edit.selectServiceType');
  }

  get workshopLabel(): string {
    if (this.editForm.get('workshop')?.value == null) {
      return this.transloco.translate('maintenanceOverview.modals.edit.selectWorkshop');
    }
    return this.selectedWorkshop || this.transloco.translate('maintenanceOverview.modals.edit.selectWorkshop');
  }

  get priorityLevelLabel(): string {
    return this.selectedPriorityLevel || this.transloco.translate('maintenanceOverview.modals.edit.selectPriority');
  }

  get maintenanceReasonLabel(): string {
    const selected = this.selectedMaintenanceReason;
    if (!selected) return this.transloco.translate('maintenanceOverview.modals.edit.selectReason');

    // The catalogue carries the localized name; until it loads, the record's own localized text is shown.
    const fromCatalogue = this.maintenanceReasons.find(r => r.name === selected)?.localizedName;
    const fromRecord = selected === this.maintenanceData?.maintenanceReason ? this.maintenanceData?.localizedMaintenanceReason : null;
    return fromCatalogue || fromRecord || selected;
  }

  populateForm(): void {
    this.selectedServiceType = this.serviceTypeLabels[this.maintenanceData.serviceType] || '';
    this.selectedMaintenanceReason = this.maintenanceData.maintenanceReason || '';

    const workshopId = this.resolveWorkshopIdByName(this.maintenanceData.workshop);
    const resolvedWorkshopName = this.workshops.find(w => w.id === workshopId)?.name;
    this.selectedWorkshop = resolvedWorkshopName || this.maintenanceData.workshop || '';

    this.selectedPriorityLevel = this.priorityLevels[this.maintenanceData.priorityLevel] || '';
    this.dueDate = this.formatDateForInput(new Date(this.maintenanceData.dueDate));

    this.editForm.patchValue({
      assetName: this.maintenanceData.assetName,
      make: this.maintenanceData.make,
      model: this.maintenanceData.model,
      source: this.maintenanceSourceLabels[this.maintenanceData.maintenanceSource] || '',
      interval: this.maintenanceData.interval
        ? this.maintenanceData.interval + ' km'
        : this.transloco.translate('maintenanceOverview.modals.edit.notAvailable'),
      distanceOver: this.maintenanceData.distanceOver
        ? this.maintenanceData.distanceOver + ' km'
        : this.transloco.translate('maintenanceOverview.modals.edit.notAvailable'),
      serviceType: this.maintenanceData.serviceType,
      workshop: workshopId,
      dueDate: this.dueDate,
      priorityLevel: this.maintenanceData.priorityLevel
    });

    this.storeInitialValues();
  }

  private storeInitialValues(): void {
    const raw = this.editForm.getRawValue();
    this.initialFormValues = {
      serviceType: raw.serviceType,
      maintenanceReason: raw.maintenanceReason,
      workshop: raw.workshop,
      dueDate: raw.dueDate,
      priorityLevel: raw.priorityLevel
    };
    this.hasChanges = false;
  }

  private checkForChanges(): void {
    if (!this.initialFormValues) return;

    const raw = this.editForm.getRawValue();
    this.hasChanges =
      raw.serviceType !== this.initialFormValues.serviceType ||
      raw.maintenanceReason !== this.initialFormValues.maintenanceReason ||
      raw.workshop !== this.initialFormValues.workshop ||
      raw.dueDate !== this.initialFormValues.dueDate ||
      raw.priorityLevel !== this.initialFormValues.priorityLevel;
  }

  async getMaintenanceReasonsAsync(): Promise<void> {
    this.isLoadingReasons = true;

    try {
      this.maintenanceReasonsSubject.next();
      const response = await this.sharedMaintenanceService.getMaintenanceReasons(this.maintenanceReasonsSubject);
      if (response.data) {
        this.maintenanceReasons = response.data.map(d => ({
          id: d.id,
          name: d.name,
          localizedName: d.localizedName
        }));

        // Pre-select the matching maintenance reason if data is available
        if (this.maintenanceData?.maintenanceReason) {
          const matchingReason = this.maintenanceReasons.find(r => r.name === this.maintenanceData.maintenanceReason);
          if (matchingReason) {
            this.editForm.get('maintenanceReason')?.setValue(matchingReason.id);
            this.selectedMaintenanceReason = matchingReason.name;
          }
        }

        // Re-store initial values now that maintenance reason has been set
        this.storeInitialValues();
      }

      this.isLoadingReasons = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoadingReasons = false;
        }
      });
    }
  }

  async getWorkshopsAsync(): Promise<void> {
    this.isLoadingWorkshops = true;

    try {
      this.workshopsSubject.next();
      const response = await this.maintenanceService.getServiceCentres(this.workshopsSubject);
      this.workshops = response.data ?? [];

      if (this.maintenanceData?.workshop) {
        const workshopId = this.resolveWorkshopIdByName(this.maintenanceData.workshop);
        const resolvedName = this.workshops.find(w => w.id === workshopId)?.name;

        this.editForm.get('workshop')?.setValue(workshopId);
        this.selectedWorkshop = resolvedName || this.maintenanceData.workshop || '';

        if (this.initialFormValues) {
          this.initialFormValues.workshop = workshopId;
          this.checkForChanges();
        } else {
          this.storeInitialValues();
        }
      }

      this.isLoadingWorkshops = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoadingWorkshops = false;
        }
      });
    }
  }

  private async loadMaintenanceDataAsync(): Promise<void> {
    await Promise.all([
      this.getMaintenanceReasonsAsync(),
      this.getWorkshopsAsync()
    ]);
  
    this.populateForm();
  }

  private resolveWorkshopIdByName(name?: string | null): number | null {
    if (!name?.trim()) return null;
    const normalize = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
    const normalized = normalize(name);
    const match = this.workshops.find(w => normalize(w.name) === normalized);

    return match?.id ?? null;
  }

  onServiceTypeSelect(type: ServiceTypes, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedServiceType = this.serviceTypeLabels[type] || '';
    this.editForm.get('serviceType')?.setValue(type);
    this.editForm.get('serviceType')?.markAsTouched();
    this.checkForChanges();
  }

  onMaintenanceReasonSelect(reason: MaintenanceReason, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedMaintenanceReason = this.maintenanceReasons.find(r => r.id === reason.id)?.name || '';
    this.editForm.get('maintenanceReason')?.setValue(reason.id);
    this.editForm.get('maintenanceReason')?.markAsTouched();
    this.checkForChanges();
  }

  onWorkshopSelect(workshop: LookupWorkshop, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedWorkshop = workshop.name;
    this.editForm.get('workshop')?.setValue(workshop.id);
    this.editForm.get('workshop')?.markAsTouched();
    this.checkForChanges();
  }

  onDueDateChange(date: string): void {
    this.dueDate = date;
    this.editForm.get('dueDate')?.setValue(date);
    this.editForm.get('dueDate')?.markAsTouched();
    this.checkForChanges();
  }

  onPriorityLevelSelect(priority: PriorityLevel, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedPriorityLevel = this.priorityLevels[priority] || '';
    this.editForm.get('priorityLevel')?.setValue(priority);
    this.checkForChanges();
  }

  formatDateForInput(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  closeModal(): void {
    this.activeModal.close();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  async onSubmit(): Promise<void> {
    if (this.editForm.invalid) {
      Object.keys(this.editForm.controls).forEach(key => {
        this.editForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isLoading = true;
    try {
      const raw = this.editForm.getRawValue();
      const payload: EditMaintenanceRequest = {
        maintenanceId: this.maintenanceData.id,
        assetId: this.maintenanceData.assetId,
        serviceType: raw.serviceType,
        dueDate: startOfDay(new Date(raw.dueDate)),
        priorityLevel: raw.priorityLevel,
        maintenanceReasonId: raw.maintenanceReason,
        workshopId: raw.workshop
      };

      await this.maintenanceService.editMaintenance(payload);
      this.toastr.success(this.transloco.translate('maintenanceOverview.toasts.maintenanceUpdated'));

      this.isLoading = false;
      this.activeModal.close(true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }
}